/**
 * Okunmamış sayaç — TEK yoklama, birden çok tüketici.
 *
 * NEDEN VAR
 * ---------
 * `Layout` ve `NotificationBell` AYNI ucu (`/notifications/unread-count`)
 * ayrı ayrı yokluyordu. 25 Temmuz'da Layout'un aralığı bilinçli olarak
 * 90 sn'ye çıkarılmış ve yorumuna yük hesabı yazılmıştı — ama zil 15 sn'de
 * yoklamaya devam ettiği için o optimizasyonu tek başına boşa çıkarıyordu.
 * 3 Eylül'de zil 60 sn'ye çekildi; bu da ara çözümdü.
 *
 * Asıl sorun aralık değil, MÜKERRERLİK. Aynı cevabı iki kez istiyorduk.
 *
 * ÖLÇEK
 * -----
 * Kullanıcı hiçbir şey yapmasa bile süren bir yoklama bu; maliyeti eşzamanlı
 * kullanıcı sayısıyla doğrusal artıyor ve uç kişiye özel olduğu için kenarda
 * ÖNBELLEKLENEMİYOR — yani her istek veritabanına iniyor.
 *
 *   önce (60 sn zil + 90 sn layout) : 180 saniyede kullanıcı başına 5 istek
 *   sonra (tek yoklama, 90 sn)      : 180 saniyede kullanıcı başına 2 istek
 *
 * 1.000 eşzamanlı kullanıcıda saniyede ~28 istekten ~11'e iniyor.
 *
 * NEDEN CONTEXT DEĞİL
 * -------------------
 * Context bir sarmalayıcı ister ve tüketicilerin o sarmalayıcının altında
 * olmasını şart koşar. Modül düzeyinde abonelik bu bağı kurmuyor: bileşen
 * ağacın neresinde olursa olsun aynı yoklamayı paylaşıyor. Bileşenlerden
 * biri ileride başka bir yere taşınırsa sessizce ikinci bir yoklama
 * başlatamaz.
 */
import { useEffect, useState } from "react";
import { apiClient, useAuth } from "../auth";

const ARALIK_MS = 90000;
// Rota değişiminde tazeleme var (rozet anında güncellensin diye). Hızlı
// gezinen kullanıcı ucu dövmesin diye alt sınır.
const EN_ERKEN_TAZELEME_MS = 5000;

const BOS = { total: 0, messages: 0 };

// ARKA PLANDAKI SEKME YOKLAMAZ — 4 Eylul 2026.
//
// Bu uc kisiye ozel: kenarda ONBELLEKLENEMIYOR, her istek veritabanina
// iniyor. Maliyeti EŞZAMANLI kullanici sayisiyla dogrusal artiyor ve
// kullanici hicbir sey yapmasa bile suruyor — 90 saniyede bir, sonsuza
// kadar, sekme arka planda unutulmus olsa bile.
//
// 50.000 kullanicinin %5'i acik olsa (2.500 kisi), 90 sn'lik aralik
// saniyede ~28 istek eder. Bunlarin buyuk kismi KIMSENIN BAKMADIGI bir
// rozet icin. Acik ama gorunmeyen sekmeyi disarida birakmak, urunde
// hicbir sey kaybettirmeden o yukun cogunu siliyor: kullanici sekmeye
// donunce zaten aninda tazeleniyor (asagidaki dinleyici).
//
// `document` yoksa (test/SSR) GORUNUR sayiliyor — yokluk, gizlilik kaniti
// degil; aksi halde varsayilan davranis sessizce degisirdi.
function _gorunur() {
  if (typeof document === "undefined") return true;
  return document.visibilityState !== "hidden";
}

let _durum = BOS;
let _aboneler = new Set();
let _zamanlayici = null;
let _sonCekim = 0;
let _ucusta = false;
let _dinliyor = false;

function _yayinla(yeni) {
  _durum = yeni;
  _aboneler.forEach((f) => f(yeni));
}

async function _cek() {
  // Aynı anda iki istek gitmesin: rota değişimi ile zamanlayıcı çakışabilir.
  if (_ucusta) return;
  // Görünmeyen sekme yoklamaz. `_sonCekim` BİLEREK güncellenmiyor: sekmeye
  // dönüldüğünde alt sınır "az önce çektik" diye tazelemeyi engellemesin.
  if (!_gorunur()) return;
  _ucusta = true;
  _sonCekim = Date.now();
  try {
    const r = await apiClient().get("/notifications/unread-count");
    _yayinla({
      total: r.data?.total || 0,
      messages: r.data?.messages || 0,
    });
  } catch (e) {
    // Sessiz: sayaç hatası kullanıcıya gösterilecek bir şey değil.
  } finally {
    _ucusta = false;
  }
}

/** Sekmeye geri dönüldüğünde bir kez tazele. Alt sınır, hızlı sekme
 * değiştiren kullanıcının ucu dövmesini engelliyor. */
function _gorunurlukDegisti() {
  if (!_gorunur()) return;
  if (Date.now() - _sonCekim < EN_ERKEN_TAZELEME_MS) return;
  _cek();
}

function _dinleyiciyiKur() {
  if (typeof document === "undefined" || _dinliyor) return;
  document.addEventListener("visibilitychange", _gorunurlukDegisti);
  _dinliyor = true;
}

function _dinleyiciyiKaldir() {
  if (typeof document === "undefined" || !_dinliyor) return;
  document.removeEventListener("visibilitychange", _gorunurlukDegisti);
  _dinliyor = false;
}

/** Rota değişimi gibi anlarda elle tazeleme. Alt sınırla korunuyor. */
export function okunmamisTazele() {
  if (_aboneler.size === 0) return;
  if (Date.now() - _sonCekim < EN_ERKEN_TAZELEME_MS) return;
  _cek();
}

/** Test/çıkış için: durumu ve zamanlayıcıyı sıfırla. */
export function okunmamisSifirla() {
  if (_zamanlayici) clearInterval(_zamanlayici);
  _zamanlayici = null;
  _dinleyiciyiKaldir();
  _sonCekim = 0;
  _ucusta = false;
  _yayinla(BOS);
}

export default function useOkunmamisSayac() {
  const { user } = useAuth();
  const [durum, setDurum] = useState(_durum);

  useEffect(() => {
    if (!user) {
      setDurum(BOS);
      return undefined;
    }
    _aboneler.add(setDurum);
    // İlk abone yoklamayı başlatır; sonrakiler mevcut değeri anında alır ve
    // İKİNCİ BİR İSTEK ATMAZ — bu bileşenin tamamının varlık sebebi.
    if (_aboneler.size === 1) {
      _cek();
      _zamanlayici = setInterval(_cek, ARALIK_MS);
      _dinleyiciyiKur();
    } else {
      setDurum(_durum);
    }
    return () => {
      _aboneler.delete(setDurum);
      if (_aboneler.size === 0) {
        clearInterval(_zamanlayici);
        _zamanlayici = null;
        _dinleyiciyiKaldir();
      }
    };
  }, [user]);

  return durum;
}
